import i18n from 'src/i18n'
import Model from 'src/models/model'
import { TYPES as SALES_CHANNEL_TYPES } from 'src/models/model/concerns/SalesChannelRestrictable'
import { AVAILABILITY_REASONS, useAvailabilityResult } from 'src/modules/checkout/composables/sales-availability/AvailabilityResult'
import { useIsItemModifierSetAvailable } from 'src/modules/checkout/composables/sales-availability/items/IsItemModifierSetAvailable'
import { useIsItemVariationAvailable } from 'src/modules/checkout/composables/sales-availability/items/IsItemVariationAvailable'
import { isOutOfStock } from 'src/services/inventory/sales-restrictions'
import { toValue } from 'vue'

// Options is a hash containing fields that can be used to validate item availability.
const DEFAULT_OPTIONS = Object.freeze({
  salesChannel: null,
  order: null
})

// TODO OOS INDY-6757
//
// The isXAvailable composables combine too many responsibilities and need to be decomposed further.
// We need distinct composables for:
// 1. This Item is Presentable (i.e. display the Item for User selection)
// 2. This Item is Selectable (i.e. the Item should be enabled/disabled for User selection)
// 3. This Item is Purchasable (?) (i.e. the Item can be purchased, either directly or via other selections, e.g. Item Packages)
// We don't want the isItemPackageAvailable composable to be making decisions about Item Variation availability,
// but isItemVariationAvailable includes unwanted conditions (i.e. we just want to validate purchasability, not presentability or selectability).
//
// Currently these concepts are tightly interwoven, making them difficult to separate correctly.
// isXAvailable is the first step towards decomposing and reusing these requirements.

export function useIsItemAvailable () {
  // TODO OOS INDY-6757 Will this react to Site changes?
  const { result } = useAvailabilityResult()
  const currentSite = Model.configuration?.currentSite

  const { isItemVariationAvailable } = useIsItemVariationAvailable()
  const { isItemModifierSetAvailable } = useIsItemModifierSetAvailable()

  /**
   * Validates whether an Item is available in the current context. An available Item can be displayed for selection.
   *
   * An available Item may still be disabled / not purchasable in some contexts.
   * E.g. Out Of Stock or Sales Disabled on PRIVATE Sales Channels (e.g. Registers).
   * This allows operators to interact with the Item if necessary (e.g. Enable Sales).
   *
   * @param {*} item The Item to validate
   * @param {*} options Contextual option values to use for validation (e.g. Sales Channel, Order)
   * @returns {Object} An AvailabilityResult object
   */
  function isItemAvailable (item, { salesChannel, order } = DEFAULT_OPTIONS) {
    const itemValue = toValue(item)

    if (_.isNil(itemValue)) {
      return result({
        availableReason: AVAILABILITY_REASONS.IS_NIL,
        availableMessage: i18n.global.t(`availability.${AVAILABILITY_REASONS.IS_NIL}`)
      })
    }

    // TODO OOS INDY-6757 Should we instead import the SalesChannel composable directly?
    // Or compute the Sales Channel once on a foundational component, to be provided and injected?
    const salesChannelValue = toValue(salesChannel)
    // TODO OOS INDY-6757 Order could be injected instead of complicating composable usage
    const orderValue = toValue(order)

    // The Item must be active (not archived) to be presented
    if (itemValue.archivable && !itemValue.isActive) {
      return result({
        availableReason: AVAILABILITY_REASONS.IS_ARCHIVED,
        availableMessage: i18n.global.t(`availability.${AVAILABILITY_REASONS.IS_ARCHIVED}`)
      })
    }

    // The Item must be available on the current Sales Channel
    if (!itemValue.isSalesChannelEnabled(salesChannelValue)) {
      return result({
        availableReason: AVAILABILITY_REASONS.IS_DISABLED_ON_SALES_CHANNEL,
        availableMessage: i18n.global.t(`availability.${AVAILABILITY_REASONS.IS_DISABLED_ON_SALES_CHANNEL}`, { salesChannel: _.startCase(salesChannelValue) })
      })
    }

    // If the Sales Channel is private, the Item is available, regardless of
    // Inventory or Sales Toggle (the Item may be shown without being selectable)
    if (_.includes(SALES_CHANNEL_TYPES.PRIVATE, salesChannelValue)) {
      return result({
        isAvailable: true
      })
    }

    if (itemValue.salesDisabled) {
      return result({
        availableReason: AVAILABILITY_REASONS.IS_SALES_DISABLED,
        availableMessage: i18n.global.t(`availability.${AVAILABILITY_REASONS.IS_SALES_DISABLED}`)
      })
    }

    // Publicly available Items are either "ready to serve" or can be delivered to the customer after an online purchase
    if (!currentSite.hasOnlineDelivery && itemValue.requiresPreparation) {
      return result({
        availableReason: AVAILABILITY_REASONS.IS_NOT_DELIVERABLE,
        availableMessage: i18n.global.t(`availability.${AVAILABILITY_REASONS.IS_NOT_DELIVERABLE}`)
      })
    }

    // TODO OOS INDY-6757 isItemVariationAvailable expects an OrderItem for isOutOfStock
    // By not providing an OrderItem, isItemVariationAvailable will not execute it's own isOutOfStock
    // Presumably isOutOfStock for Item validates contingent stock requirements, somehow
    const isAnyItemVariationAvailable = _.some(itemValue.itemVariations?.data, itemVariation => isItemVariationAvailable(itemVariation, { salesChannel: salesChannelValue }).isAvailable)
    if (!isAnyItemVariationAvailable) {
      return result({
        availableReason: AVAILABILITY_REASONS.IS_SELECTION_REQUIREMENTS_UNMET,
        availableMessage: i18n.global.t(`availability.${AVAILABILITY_REASONS.IS_SELECTION_REQUIREMENTS_UNMET}`)
      })
    }

    const areAllRequiredModifiersAvailable = _.every(itemValue.itemModifierSets?.data, itemModifierSet =>
      !itemModifierSet.isRequired || isItemModifierSetAvailable(itemModifierSet, { salesChannel: salesChannelValue }).isAvailable
    )
    if (!areAllRequiredModifiersAvailable) {
      return result({
        availableReason: AVAILABILITY_REASONS.IS_SELECTION_REQUIREMENTS_UNMET,
        availableMessage: i18n.global.t(`availability.${AVAILABILITY_REASONS.IS_SELECTION_REQUIREMENTS_UNMET}`)
      })
    }

    if (!_.isNil(orderValue) && currentSite?.activeInventoryStrategy?.excludeOutOfStockItemsForKioskAndConsumer) {
      if (isOutOfStock(itemValue, { order: orderValue })) {
        return result({
          availableReason: AVAILABILITY_REASONS.IS_OUT_OF_STOCK,
          availableMessage: i18n.global.t(`availability.${AVAILABILITY_REASONS.IS_OUT_OF_STOCK}`)
        })
      }
    }

    return result({
      isAvailable: true
    })
  }

  return {
    isItemAvailable
  }
}
